import React from 'react';
import { Box, Typography, Slider, Divider, TextField, MenuItem, Grid, InputAdornment, useTheme } from '@mui/material';
import { usePhotoAddsStore } from '../usePhotoAddsStoreBridge';

const PropertiesPanel = () => {
  const theme = useTheme();
  const {
    // adjustments
    opacity, setOpacity, brightness, setBrightness, contrast, setContrast, saturation, setSaturation,
    fontSize, setFontSize, fontFamily, setFontFamily,
    fillColor, setFillColor, strokeColor, setStrokeColor,
    // canvas / selection
    canvas, selectedLayer, updateLayersFromCanvas,
  } = usePhotoAddsStore();

  const activeObject = canvas?.getActiveObject?.() || selectedLayer;
  const objType = activeObject?.type;
  // Only show text controls when an actual text object is active
  const isTextContext = objType === 'i-text';
  const isLine = objType === 'line';
  const isShape = objType && ['rect', 'circle', 'triangle', 'polygon', 'ellipse', 'path'].includes(objType);
  const isImage = objType === 'image';

  const applyToActiveText = (updates) => {
    const obj = canvas?.getActiveObject();
    if (obj && obj.type === 'i-text') {
      obj.set({ ...updates });
      canvas.renderAll();
      updateLayersFromCanvas?.();
    }
  };

  const applyToActiveShape = (updates) => {
    const obj = canvas?.getActiveObject();
    if (obj && obj.type !== 'i-text' && obj.type !== 'image') {
      obj.set({ ...updates });
      canvas.renderAll();
      updateLayersFromCanvas?.();
    }
  };

  return (
    <Box sx={{ color: theme.palette.text.primary, p: 2 }}>
      {isTextContext && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Text</Typography>
          <TextField
            select
            label="Font"
            value={fontFamily}
            onChange={(e) => { setFontFamily(e.target.value); applyToActiveText({ fontFamily: e.target.value }); }}
            size="small"
            fullWidth
            sx={{ mb: 1, '& .MuiInputBase-root': { color: theme.palette.text.primary }, '& .MuiInputLabel-root': { color: theme.palette.text.secondary } }}
          >
            {['Arial', 'Helvetica', 'Times New Roman', 'Georgia', 'Courier New', 'Impact', 'Inter', 'Roboto'].map((f) => (
              <MenuItem key={f} value={f}>
                <span style={{ fontFamily: f }}>{f}</span>
              </MenuItem>
            ))}
          </TextField>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, width: '100px' }}>Font Size:</Typography>
            <Slider 
              value={fontSize} 
              onChange={(e, v) => { setFontSize(v); applyToActiveText({ fontSize: v }); }} 
              min={8} 
              max={160} 
              valueLabelDisplay="auto" 
              sx={{ width: 'calc(100% - 120px)', ml: 2 }} 
              valueLabelFormat={(value) => `${value}px`}
            />
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, width: '20px', textAlign: 'right' }}>{fontSize}px</Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>Fill</Typography>
              <input 
                type="color" 
                value={fillColor} 
                onChange={(e) => { setFillColor(e.target.value); applyToActiveText({ fill: e.target.value }); }} 
                style={{ width: '100%', height: '30px', border: 'none', borderRadius: '4px' }}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>Stroke</Typography>
              <input 
                type="color" 
                value={strokeColor} 
                onChange={(e) => { setStrokeColor(e.target.value); applyToActiveText({ stroke: e.target.value }); }} 
                style={{ width: '100%', height: '30px', border: 'none', borderRadius: '4px' }}
              />
            </Grid>
          </Grid>
          <Divider sx={{ mt: 2, mb: 1, borderColor: theme.palette.divider }} />
        </Box>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2" sx={{ width: '100px' }}>Opacity</Typography>
        <Slider
          value={opacity}
          onChange={(e, v) => {
            setOpacity(v);
            const obj = canvas?.getActiveObject();
            if (obj) { obj.set({ opacity: (Array.isArray(v) ? v[0] : v) / 100 }); canvas.renderAll(); }
          }}
          min={0}
          max={100}
          valueLabelDisplay="auto"
          sx={{ width: 'calc(100% - 120px)', ml: 2 }}
          valueLabelFormat={(value) => `${value}%`}
        />
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, width: '20px', textAlign: 'right' }}>{opacity}%</Typography>
      </Box>

      {/* Shape/Line controls when a non-text, non-image object is selected */}
      {(isShape || isLine) && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>{isLine ? 'Line' : 'Shape'} Properties</Typography>
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xs={6}>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>Fill</Typography>
              <input 
                type="color" 
                value={fillColor} 
                onChange={(e) => { setFillColor(e.target.value); applyToActiveShape({ fill: e.target.value }); }} 
                style={{ width: '100%', height: '30px', border: 'none', borderRadius: '4px' }}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>Stroke</Typography>
              <input 
                type="color" 
                value={strokeColor} 
                onChange={(e) => { setStrokeColor(e.target.value); applyToActiveShape({ stroke: e.target.value }); }} 
                style={{ width: '100%', height: '30px', border: 'none', borderRadius: '4px' }}
              />
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, width: '100px' }}>Opacity:</Typography>
            <Slider 
              min={0} 
              max={100} 
              valueLabelDisplay="auto" 
              onChange={(e, v) => applyToActiveShape({ opacity: (Array.isArray(v) ? v[0] : v) / 100 })} 
              sx={{ width: 'calc(100% - 120px)', ml: 2 }}
              valueLabelFormat={(value) => `${value}%`}
            />
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, width: '20px', textAlign: 'right' }}>{activeObject?.opacity * 100 || 0}%</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, width: '100px' }}>Stroke Width:</Typography>
            <Slider 
              min={0} 
              max={50} 
              valueLabelDisplay="auto" 
              onChange={(e, v) => applyToActiveShape({ strokeWidth: Array.isArray(v) ? v[0] : v })} 
              sx={{ width: 'calc(100% - 120px)', ml: 2 }}
              valueLabelFormat={(value) => `${value}px`}
            />
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, width: '20px', textAlign: 'right' }}>
              {activeObject?.strokeWidth || 0}px
            </Typography>
          </Box>
          <Divider sx={{ mt: 2, mb: 1, borderColor: theme.palette.divider }} />
        </Box>
      )}

      {/* Image adjustments only when image selected; keep available otherwise if you want global */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2" sx={{ width: '100px' }}>Brightness</Typography>
        <Slider 
          value={brightness} 
          onChange={(e, v) => setBrightness(v)} 
          min={0} 
          max={200} 
          valueLabelDisplay="auto" 
          sx={{ width: 'calc(100% - 120px)', ml: 2 }}
          valueLabelFormat={(value) => `${value}%`}
        />
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, width: '20px', textAlign: 'right' }}>{brightness}%</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2" sx={{ width: '100px' }}>Contrast</Typography>
        <Slider 
          value={contrast} 
          onChange={(e, v) => setContrast(v)} 
          min={0} 
          max={200} 
          valueLabelDisplay="auto" 
          sx={{ width: 'calc(100% - 120px)', ml: 2 }}
          valueLabelFormat={(value) => `${value}%`}
        />
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, width: '20px', textAlign: 'right' }}>{contrast}%</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="subtitle2" sx={{ width: '100px' }}>Saturation</Typography>
        <Slider 
          value={saturation} 
          onChange={(e, v) => setSaturation(v)} 
          min={0} 
          max={200} 
          valueLabelDisplay="auto" 
          sx={{ width: 'calc(100% - 120px)', ml: 2 }}
          valueLabelFormat={(value) => `${value}%`}
        />
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, width: '20px', textAlign: 'right' }}>{saturation}%</Typography>
      </Box>
    </Box>
  );
};

export default PropertiesPanel;
